/**
 * Where an operator's typing becomes a route.
 *
 * The box takes a complete identifier, a fragment of one, or a name — so the
 * first thing it has to decide is which of those it was given. A complete
 * identifier is an **address**: it goes straight to the entity screen, which
 * serves a taxpayer nobody has ever asked about, and that is the workflow
 * support arrives with (a NIP carried across from an order screen). Anything
 * else is a **query**, and belongs in the list, where it can match a name, a
 * REGON, a KRS number or part of any of them.
 *
 * A bare number is read as Polish, matching the API contract, and the country
 * is kept rather than stripped — the panel must not be the place where the key
 * quietly becomes PL-only.
 */
import { hasValidator, parseTaxId } from "@sauron/contracts"

export interface LookupTarget {
  readonly country: string
  readonly taxId: string
}

export type Lookup =
  /** A complete identifier. The entity screen answers for it, known or not. */
  | { readonly kind: "entity"; readonly target: LookupTarget }
  /** A fragment, a name, anything else. Only the list can answer it. */
  | { readonly kind: "search"; readonly text: string }

/** Ten digits, which is what a NIP is. A REGON is nine or fourteen, so it searches. */
const COMPLETE_PL = /^[0-9]{10}$/
/** An explicit country and digits: they typed an address, in a format we do not know. */
const PREFIXED = /^([A-Z]{2})([0-9]+)$/
/** Two letters and then something — the shape of any prefixed identifier at all. */
const HAS_PREFIX = /^[A-Z]{2}[0-9A-Z+*]+$/

export function readLookup(raw: string): Lookup | null {
  const text = raw.trim()
  if (text === "") return null

  const compact = text.replace(/[\s.-]/g, "").toUpperCase()

  /*
   * **A prefixed identifier the boundary itself would accept is an address**,
   * whatever characters it carries: `ATU12345678`, `IE6388047V`,
   * `NL810433941B01` all have letters where the digits-only shape below sees
   * none. The parser is the same one `/v1/decisions` runs, so the panel cannot
   * disagree with the API about what is a complete identifier — and it is only
   * trusted where a validator is registered, because for any other pair of
   * letters `parseTaxId` would read the start of a company name as a country.
   */
  if (HAS_PREFIX.test(compact)) {
    const parsed = parseTaxId(compact)
    if (parsed !== null && hasValidator(parsed.country)) {
      return { kind: "entity", target: { country: parsed.country, taxId: parsed.value } }
    }
  }

  const prefixed = PREFIXED.exec(compact)
  if (prefixed !== null) {
    return {
      kind: "entity",
      target: { country: prefixed[1] ?? "PL", taxId: prefixed[2] ?? compact },
    }
  }
  if (COMPLETE_PL.test(compact)) {
    return { kind: "entity", target: { country: "PL", taxId: compact } }
  }
  return { kind: "search", text }
}
